<script lang="ts">
  import Screen from '../lib/design/Screen.svelte';
  import AnswerBody from '../lib/design/AnswerBody.svelte';
  import PeekSheet from '../lib/design/PeekSheet.svelte';
  import { link, href } from '../lib/router.svelte';
  import { nest } from '../lib/nest.svelte';
  import { humanize } from '../shared/slug.js';

  let { slug }: { slug: string } = $props();

  const page = $derived(nest.get(slug));
  const backlinks = $derived(nest.backlinks(slug));

  let peeked = $state<string | null>(null);
</script>

<Screen title="Read" sync={nest.status}>
  {#if page}
    <article>
      <header>
        <h1>{humanize(page.slug)}</h1>
        <p class="meta">
          <span class="chip">{page.type}</span>
          {#each page.tags as t (t)}<span class="chip tag">#{t}</span>{/each}
          {#if page.updated}<span class="when">updated {page.updated}</span>{/if}
        </p>
      </header>

      <AnswerBody text={page.body} onpeek={(s) => (peeked = s)} />

      {#if backlinks.length}
        <section class="backlinks">
          <h2>Linked from</h2>
          <ul>
            {#each backlinks as b (b.slug)}
              <li><a use:link href={href(`/page/${b.slug}`)}>{humanize(b.slug)}</a></li>
            {/each}
          </ul>
        </section>
      {/if}
    </article>
  {:else if !nest.hydrated}
    <p class="hint">Loading…</p>
  {:else}
    <div class="missing">
      <h1>{humanize(slug)}</h1>
      <p class="hint">
        This page isn't in your nest{nest.count === 0 ? ' yet — pull it in from Read' : ''}.
      </p>
      <a class="back" use:link href={href('/read')}>← Back to Read</a>
    </div>
  {/if}
</Screen>

{#if peeked}
  <PeekSheet slug={peeked} onclose={() => (peeked = null)} />
{/if}

<style>
  header h1 {
    font-family: var(--serif);
    font-size: 27px;
    line-height: 1.2;
    letter-spacing: -0.01em;
  }
  .meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin: 8px 0 20px;
  }
  .chip {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 2px 7px;
    border-radius: 999px;
    background: var(--paper);
    color: var(--ink-soft);
  }
  .chip.tag {
    background: none;
    border: 1px solid var(--line);
  }
  .when {
    font-size: 11px;
    color: var(--ink-faint);
  }
  .backlinks {
    margin-top: 32px;
    border-top: 1px solid var(--line);
    padding-top: 16px;
  }
  .backlinks h2 {
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink-soft);
    margin-bottom: 10px;
  }
  .backlinks ul {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .backlinks a {
    color: var(--rooster);
    font-family: var(--serif);
    font-size: 15px;
  }
  .hint {
    color: var(--ink-soft);
    font-size: 14px;
  }
  .missing h1 {
    font-family: var(--serif);
    font-size: 24px;
    margin-bottom: 8px;
  }
  .back {
    display: inline-block;
    margin-top: 14px;
    color: var(--rooster);
    font-family: var(--mono);
    font-size: 12px;
  }
</style>
